import {useEffect, useRef, useState} from 'react'

import Auth0Lock from 'auth0-lock'

import {loginFailed} from '../actions'
import HydrolinkLogo from '../assets/LOGO_login_menu.svg'
import {AppEnv} from '../conf/appEnv'
import {AUTH0_CLIENT_ID, LOGIN_DOMAIN} from '../constants/auth'
import {useAppDispatch, useAppSelector} from '../hooks'
import {redirectUrl} from '../redirectUrl'
import {persistentStorage} from '../utils/persistence-storage'

/**
 * ID of the container element where Auth0 SDK renders the login form.
 */
export const authFormContainerId = 'auth0-login'

export interface Auth0LockInstance {
  show: () => void
  hide: () => void
}

export function useAuth0Lock(
  onAuthenticated: (authResult: AuthResult) => void,
  onAuthorizationError: (err: string) => void
): null | Auth0LockInstance {
  const dispatch = useAppDispatch()
  const callbacks = useRef({onAuthenticated, onAuthorizationError})
  const [lock, setLock] = useState<Auth0LockInstance | null>(null)
  const previousLoginError = persistentStorage.previousLoginError.getMessage()
  const authToken = useAppSelector(state => state.auth.token)

  useEffect(() => {
    const newLock = new Auth0Lock(AUTH0_CLIENT_ID ?? '', LOGIN_DOMAIN ?? '', {
      language: AppEnv.defaultLang,
      theme: {
        primaryColor: '#2C7BE5',
        logo: HydrolinkLogo
      },
      languageDictionary: {
        title: 'Kirjaudu sisään',
        signUpTitle: 'Rekisteröidy'
      },
      auth: {
        redirectUrl: redirectUrl(),
        responseType: 'token id_token',
        params: {
          scope: 'openid email app_metadata'
        }
      },
      allowSignUp: true,
      container: authFormContainerId,
      avatar: null
    })

    newLock.on('authenticated', (authResult: AuthResult) => {
      callbacks.current.onAuthenticated(authResult)
    })

    newLock.on('authorization_error', (err: string) => {
      callbacks.current.onAuthorizationError(err)
    })

    newLock.on('show', () => {
      //console.debug('Show auth lock')
    })
    newLock.on('hide', () => {
      //console.debug('Hide auth lock')
    })

    newLock.on('unrecoverable_error', (err: string) => {
      console.error(`Unrecoverable auth error: ${err}`)
    })

    // note: passing the 'show' call into the end of event queue fixes random rendering issues that makes
    // the login form fields partially hidden.
    const cancelId = setTimeout(() => {
      newLock.show()
    }, 0)

    setLock(newLock)
    return () => clearTimeout(cancelId)
  }, [])

  useEffect(() => {
    if (previousLoginError) {
      // Re-dispatch error loaded from local storage into error state
      persistentStorage.previousLoginError.clear()
      dispatch(loginFailed(previousLoginError))
    }
  }, [dispatch, previousLoginError])

  useEffect(() => {
    callbacks.current = {
      onAuthenticated,
      onAuthorizationError
    }
  }, [onAuthenticated, onAuthorizationError])

  useEffect(() => {
    if (!authToken && lock != null) {
      // note: passing the 'show' call into the end of event queue fixes random rendering issues that makes
      // the login form fields partially hidden.
      const cancelId = setTimeout(() => {
        if (lock) {
          lock.show()
        }
      }, 0)

      return () => clearTimeout(cancelId)
    }
  }, [authToken, lock])

  return lock
}
